import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import './styles/PageTransition.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LinkedInEmbed from './components/LinkedInEmbed';
import TeamSection from './components/TeamSection';
import Services from './components/Services';
import ImpactStory from './components/ImpactStory';
import Blog from './components/Blog';
import Resources from './components/Resources';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Login from './components/Login';
import ClientResources from './components/ClientResources';
import AdminPanel from './components/AdminPanel';
import TermsConditions from './components/TermsConditions';
import ArticlePage from './components/ArticlePage';
import CommunityDevelopment2025 from './components/CommunityDevelopment2025';
import { AuthProvider } from './components/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import { scrollToSection } from './utils/scroll';
import SEO from './components/SEO';
import NmtcClimateJustice from './components/insights/NmtcClimateJustice';
import { analytics } from './firebase';
import { logEvent } from 'firebase/analytics';
import LangchainChatbot from './components/LangchainChatbot.jsx';

// PageTransition component to handle fade transitions between pages
const PageTransition = ({ children }) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");
  
  useEffect(() => {
    // Only trigger transition for actual page changes, not hash changes
    if (location !== displayLocation && location.pathname !== displayLocation.pathname) {
      // Skip transition if navigating to/from the home page with hash
      const isHashNavigation = location.hash || displayLocation.hash;
      const isHomePage = location.pathname === '/' || displayLocation.pathname === '/';
      
      if (!(isHashNavigation && isHomePage)) {
        setTransitionStage("fadeOut");
      }
    }
  }, [location, displayLocation]);
  
  const handleAnimationEnd = () => {
    if (transitionStage === "fadeOut") {
      setTransitionStage("fadeIn");
      setDisplayLocation(location);
    }
  };
  
  return (
    <div 
      className={`page-transition ${transitionStage}`} 
      onAnimationEnd={handleAnimationEnd}
    >
      {children}
    </div>
  );
};

// ScrollToTop component to handle scrolling to top on route changes
const ScrollToTop = () => {
  const location = useLocation();
  const { state, hash } = location;
  
  useEffect(() => {
    // If specific section is provided in navigation state, scroll to that section
    if (state?.scrollTo) {
      setTimeout(() => {
        scrollToSection(state.scrollTo);
      }, 100); // Short delay to ensure component is mounted
    } else if (hash) {
      // Handle hash links (e.g., /#services)
      const sectionId = hash.substring(1); // Remove the # character
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    } else if (location.pathname === '/') {
      // On homepage without hash, scroll to top
      window.scrollTo(0, 0);
    } else {
      // On other pages, scroll to top
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash, state, hash]);
  
  return null;
};

// SEO component with current route pathname
const RouteAwareSEO = () => {
  const location = useLocation();
  
  // This ensures the SEO component re-renders whenever the location changes
  useEffect(() => {
    // Empty dependency array would only run once
    // By including location.pathname, we ensure SEO updates on route changes
  }, [location.pathname]);
  
  return <SEO pathname={location.pathname} />;
};

// Page view tracker
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    try {
      logEvent(analytics, 'page_view', {
        page_path: location.pathname + location.search,
      });
    } catch (e) {
      // analytics may not be initialized in some environments
      console.error('Analytics error', e);
    }
  }, [location]);

  return null;
};

function App() {
  // Move baseUrl to state and initialize with '/'
  const [baseUrl, setBaseUrl] = useState('/');
  
  // Get the base URL in useEffect to avoid DOM access during render
  useEffect(() => {
    try {
      const baseElement = document.getElementsByTagName('base')[0];
      if (baseElement) {
        const href = baseElement.getAttribute('href');
        if (href) {
          setBaseUrl(href);
        }
      }
    } catch (error) {
      console.error('Error getting base URL:', error);
    }
  }, []);
  
  return (
    <AuthProvider>
      <Router basename={baseUrl}>
        <div className="App">
          <a href="#main-content" className="skip-to-content">Skip to main content</a>
          <ScrollToTop />
          <RouteAwareSEO />
          <PageViewTracker />
          <PageTransition>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/article/:articleId" element={<ArticlePage />} />
              <Route path="/community-development-2025" element={<CommunityDevelopment2025 />} />
              <Route path="/client-resources" element={
                <PrivateRoute>
                  <ClientResources />
                </PrivateRoute>
              } />
              <Route path="/nmtc-climate-justice" element={<NmtcClimateJustice />} />
              <Route path="/community-development-2025-direct" element={
                <>
                  <Navbar />
                  <CommunityDevelopment2025 />
                  <Footer />
                </>
              } />
              <Route path="/" element={
                <>
                  <Navbar />
                  <main id="main-content">
                    <Hero />
                    <LinkedInEmbed />
                    <TeamSection />
                    <Services />
                    <ImpactStory />
                    <Blog />
                    <Resources />
                    <Contact />
                    <LangchainChatbot />
                  </main>
                  <Footer />
                </>
              } />
            </Routes>
          </PageTransition>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;